import { useState, useEffect, useCallback } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  CustomerFormCustomer,
  SearchResultCustomer,
} from './types';

interface UseCustomerSearchOptions {
  supabase: SupabaseClient;
  selectedCustomer: CustomerFormCustomer | null;
  onCustomerLoaded: (customer: CustomerFormCustomer) => void;
  onClearSelection: () => void;
  formEmail: string;
  /**
   * Company name as typed in the form. Used alongside email for the duplicate
   * check so a fresh contact at a known legal entity still surfaces the
   * existing customer record. Optional, defaults to '' (email-only check).
   */
  formCompanyName?: string;
}

export function useCustomerSearch({
  supabase,
  selectedCustomer,
  onCustomerLoaded,
  onClearSelection,
  formEmail,
  formCompanyName = '',
}: UseCustomerSearchOptions) {
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResultCustomer[]>([]);
  const [searching, setSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Duplicate check state
  const [duplicateCustomer, setDuplicateCustomer] = useState<CustomerFormCustomer | null>(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  // Messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // ==========================================================================
  // SEARCH EXISTING CUSTOMERS
  // ==========================================================================

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        searchCustomers(searchTerm);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  async function searchCustomers(query: string) {
    try {
      setSearching(true);

      // Same RPC as the booking wizard so customers + open leads come back
      // tagged with `source` ('customer' or 'lead'). Keeps the two flows
      // behaving identically.
      const { data, error: searchError } = await supabase
        .rpc('search_customers_global', { p_query: query, p_limit: 50 });

      if (searchError) throw searchError;

      setSearchResults((data || []) as SearchResultCustomer[]);
      setShowSearchResults(true);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  async function handleSelectCustomer(searchResult: SearchResultCustomer | CustomerFormCustomer) {
    try {
      setSearching(true);

      // Lead pick — no customer record yet. Pass the lead row through as-is
      // so the form pre-fills from name/email/phone. The lead_id rides on
      // the saved record so the backend can mark it converted on save.
      const source = (searchResult as { source?: string }).source;
      if (source === 'lead') {
        onCustomerLoaded({
          ...(searchResult as unknown as CustomerFormCustomer),
          source: 'lead',
          lead_id: searchResult.id,
        } as CustomerFormCustomer);
        setSearchTerm('');
        setSearchResults([]);
        setShowSearchResults(false);
        setDuplicateCustomer(null);
        setSuccessMessage(`Loaded lead: ${searchResult.customer_name}`);
        setTimeout(() => setSuccessMessage(null), 3000);
        return null;
      }

      const result = await supabase
        .from('customers')
        .select('*')
        .eq('id', searchResult.id)
        .single();

      const error = result.error;
      const fullCustomer = result.data;

      if (error) {
        console.error('Error fetching full customer:', error);
        return 'Failed to load customer details. Please try again.';
      }

      if (!fullCustomer) {
        return 'Customer not found. They may have been deleted.';
      }

      onCustomerLoaded(fullCustomer);
      setSearchTerm('');
      setSearchResults([]);
      setShowSearchResults(false);
      setDuplicateCustomer(null);

      const ownershipNote = fullCustomer.is_own_customer === false
        ? ' (from another territory)'
        : '';
      setSuccessMessage(`Loaded: ${fullCustomer.customer_name}${ownershipNote}`);
      setTimeout(() => setSuccessMessage(null), 3000);

      return null; // no error
    } catch (err) {
      console.error('Error selecting customer:', err);
      return 'Failed to load customer. Please try again.';
    } finally {
      setSearching(false);
    }
  }

  /**
   * Bring a retired customer back and load them into the form. This is the
   * only route by which a retired record re-enters use, and it is always a
   * deliberate click, never automatic. Returns an error string for the caller
   * to display, or null on success.
   */
  async function handleRestoreCustomer(customer: CustomerFormCustomer): Promise<string | null> {
    if (!customer.id) return 'That customer cannot be restored because the record is incomplete.';

    try {
      setSearching(true);

      const { data, error } = await supabase
        .from('customers')
        .update({ status: 'active', updated_at: new Date().toISOString() })
        .eq('id', customer.id)
        .select()
        .single();

      if (error) throw error;
      if (!data) return 'Customer not found. They may have been deleted.';

      onCustomerLoaded(data);
      setSearchTerm('');
      setSearchResults([]);
      setShowSearchResults(false);
      setDuplicateCustomer(null);

      setSuccessMessage(`Restored: ${data.customer_name}`);
      setTimeout(() => setSuccessMessage(null), 3000);

      return null;
    } catch (err) {
      console.error('Error restoring customer:', err);
      return 'Could not restore that customer. Please try again.';
    } finally {
      setSearching(false);
    }
  }

  function handleClearSelection() {
    setDuplicateCustomer(null);
    onClearSelection();
  }

  // ==========================================================================
  // DUPLICATE CHECK ON EMAIL
  // ==========================================================================

  // Duplicate check on email + company name. Either signal counts as a near
  // match; email is the strongest, company name catches "different contact at
  // the same business" before a duplicate customer record is created.
  const checkDuplicate = useCallback(async (email: string, companyName: string) => {
    const emailReady = email && email.length >= 5 && email.includes('@');
    const companyReady = companyName && companyName.trim().length >= 3;
    if (!emailReady && !companyReady) {
      setDuplicateCustomer(null);
      return;
    }

    // Already editing this exact email, no point flagging it as a duplicate of itself.
    if (emailReady && selectedCustomer?.email?.toLowerCase() === email.toLowerCase()) {
      setDuplicateCustomer(null);
      return;
    }

    try {
      setCheckingDuplicate(true);

      const filters: string[] = [];
      if (emailReady) filters.push(`email.ilike.${email}`);
      if (companyReady) {
        // Escape the comma / parenthesis edge cases that would break the
        // PostgREST or(...) syntax. Trim and lower the comparison.
        const safe = companyName.trim().replace(/[,()]/g, ' ');
        filters.push(`company_name.ilike.${safe}`);
      }

      // Retired customers are included on purpose. A retired record (the
      // "delete customer" action sets status = 'inactive') is hidden from the
      // list and the search box, but it still holds its email address, and the
      // unique index on email does not care whether a record is retired. Left
      // out of this check, a retired customer produced no warning at all and
      // the save was then rejected by the database with nothing useful to show
      // the user. It is surfaced here instead, labelled as retired, and can
      // only be reused by explicitly restoring it — never loaded silently.
      // Active matches sort first ('active' < 'inactive') so a live customer
      // always wins over a retired one.
      const query = supabase
        .from('customers')
        .select('*')
        .or(filters.join(','))
        .order('status', { ascending: true });
      if (selectedCustomer?.id) query.neq('id', selectedCustomer.id);
      const { data, error } = await query.limit(1);

      if (error) throw error;

      if (data && data.length > 0) {
        setDuplicateCustomer(data[0]);
      } else {
        setDuplicateCustomer(null);
      }
    } catch (err) {
      console.error('Duplicate check error:', err);
    } finally {
      setCheckingDuplicate(false);
    }
  }, [supabase, selectedCustomer]);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkDuplicate(formEmail, formCompanyName);
    }, 500);

    return () => clearTimeout(timer);
  }, [formEmail, formCompanyName, checkDuplicate]);

  return {
    searchTerm,
    setSearchTerm,
    searchResults,
    searching,
    showSearchResults,
    duplicateCustomer,
    checkingDuplicate,
    successMessage,
    handleSelectCustomer,
    handleRestoreCustomer,
    handleClearSelection,
  };
}
